import { ReviewStatus } from '@skop-app/lib-types'
import { isOptionSelectableStatus } from '@/services/property/option.status'
import type { IndexedReference } from '@/services/reference/reference.type'
import type { Context, GridContextOption } from '@/types/grid'

type SyncGridContextOptionParams = {
  context: Context
  fieldKey: string
  option: {
    id: string
    value: string
    label: string
    status?: string
  }
  parentOptionId?: string
}

const sortOptionsByLabel = <T extends { label?: string }>(options: T[]): T[] =>
  [...options].sort((left, right) =>
    (left.label ?? '').localeCompare(right.label ?? '', undefined, {
      sensitivity: 'base',
    }),
  )

const matchesOptionNode = (
  candidate: Pick<GridContextOption, 'id' | 'value'>,
  targetId: string,
) => candidate.id === targetId || candidate.value === targetId

const buildGridContextOption = ({
  id,
  value,
  label,
  status,
}: SyncGridContextOptionParams['option']): GridContextOption => ({
  id,
  value,
  label,
  status,
  isInReview: status === ReviewStatus.PENDING,
  isSelectable: isOptionSelectableStatus(status),
})

const upsertOptionNodes = ({
  nodes,
  nextOption,
  parentOptionId,
}: {
  nodes: GridContextOption[] | undefined
  nextOption: GridContextOption
  parentOptionId?: string
}): GridContextOption[] => {
  const currentNodes = Array.isArray(nodes) ? nodes : []
  const targetId = nextOption.id
  let didUpdateExistingOption = false
  let didInsertIntoParent = false

  const visit = (items: GridContextOption[]): GridContextOption[] =>
    items.map((item) => {
      const currentChildren = Array.isArray(item.options)
        ? (item.options as GridContextOption[])
        : undefined

      if (matchesOptionNode(item, targetId)) {
        didUpdateExistingOption = true
        return {
          ...item,
          ...nextOption,
          options: currentChildren,
        }
      }

      if (parentOptionId && matchesOptionNode(item, parentOptionId)) {
        const nextChildren = upsertOptionNodes({
          nodes: currentChildren,
          nextOption,
        })
        didInsertIntoParent = true
        return {
          ...item,
          options: sortOptionsByLabel(nextChildren),
        }
      }

      if (!currentChildren) return item

      const nextChildren = visit(currentChildren)
      if (nextChildren === currentChildren) return item

      return {
        ...item,
        options: nextChildren,
      }
    })

  const visitedNodes = visit(currentNodes)

  if (didUpdateExistingOption || didInsertIntoParent) {
    return visitedNodes
  }

  if (parentOptionId) {
    return currentNodes
  }

  return sortOptionsByLabel([...visitedNodes, nextOption])
}

export const syncCreatedOptionIntoGridContext = ({
  context,
  fieldKey,
  option,
  parentOptionId,
}: SyncGridContextOptionParams) => {
  if (!fieldKey || !option.id) return

  const nextContextOption = buildGridContextOption(option)
  const refLookupKey = fieldKey === 'action' ? option.value : option.id

  Reflect.set(context, 'propertyOptions', {
    ...(context.propertyOptions ?? {}),
    [fieldKey]: upsertOptionNodes({
      nodes: context.propertyOptions?.[fieldKey],
      nextOption: nextContextOption,
      parentOptionId,
    }),
  })

  Reflect.set(context, 'propertyDisplayOptions', {
    ...(context.propertyDisplayOptions ?? {}),
    [fieldKey]: upsertOptionNodes({
      nodes: context.propertyDisplayOptions?.[fieldKey],
      nextOption: nextContextOption,
      parentOptionId,
    }),
  })

  Reflect.set(context, 'refDataByKey', {
    ...(context.refDataByKey ?? {}),
    [fieldKey]: {
      ...(context.refDataByKey?.[fieldKey] ?? {}),
      [refLookupKey]: option.label,
    },
  })

  Reflect.set(context, 'optionsByID', {
    ...(context.optionsByID ?? {}),
    [fieldKey]: {
      ...(context.optionsByID?.[fieldKey] ?? {}),
      [option.id]: {
        ...(context.optionsByID?.[fieldKey]?.[option.id] ?? {}),
        id: option.id,
        value: option.value,
        label: option.label,
        status: option.status,
        isSelectable: isOptionSelectableStatus(option.status),
      },
    },
  })
}

type SyncGridContextReferenceParams = {
  context: Context
  label: string
  reference: IndexedReference
}

export const syncCreatedReferenceIntoGridContext = ({
  context,
  label,
  reference,
}: SyncGridContextReferenceParams) => {
  if (!reference.id) return

  Reflect.set(context, 'referencesById', {
    ...(context.referencesById ?? {}),
    [reference.id]: reference,
  })

  Reflect.set(context, 'referenceLabelsById', {
    ...(context.referenceLabelsById ?? {}),
    [reference.id]: label || reference.id,
  })
}
